/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this 
 * file in accordance with the terms of the Adobe license agreement 
 * accompanying it. 
 *******************************************************************/

import {
  HTMLAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
  createPortal,
  useRef,
} from 'preact/compat';
import {
  Container,
  Slot,
  SlotProps,
} from '@adobe-commerce/elsie/lib';
import { formattedDate } from '@/quote-management/utils/dateUtils';
import { events } from '@adobe-commerce/event-bus';
import { useText } from '@adobe-commerce/elsie/i18n';
import {
  Button,
  TextArea,
  InLineAlert,
  Icon,
  InputFile,
} from '@adobe-commerce/elsie/components';
import { NegotiableQuoteTemplateModel, NegotiableQuoteTemplateStatus } from '@/quote-management/data/models/negotiable-quote-template-model';
import {
  TabbedContent,
  ActionsBar,
  ConfirmationModal,
  ManageNegotiableQuoteTemplate as ManageNegotiableQuoteTemplateComponent,
  ManageNegotiableQuoteTemplateProps as ManageNegotiableQuoteTemplateComponentProps,
  ReferenceDocumentsList,
  ReferenceDocumentFormModal,
  ReferenceDocument,
  AttachedFilesList,
  AttachedFile,
} from '@/quote-management/components';
import {
  ItemsQuotedTemplate,
  QuoteTemplateCommentsList,
  QuoteTemplateHistoryLog,
  ShippingAddressDisplay,
} from '@/quote-management/containers';
import {
  WarningFilled,
  CheckWithCircle,
  Edit,
  Add,
} from '@adobe-commerce/elsie/icons';
import {
  sendQuoteTemplateForReview,
  deleteQuoteTemplate,
  cancelQuoteTemplate,
  generateQuoteFromTemplate,
  openQuoteTemplate,
  acceptQuoteTemplate,
  uploadFile,
  ReferenceDocumentLinkInput,
} from '@/quote-management/api';
import { useReferenceDocumentLinks } from '@/quote-management/hooks/useReferenceDocumentLinks';
import { useRenameTemplate } from '@/quote-management/hooks/useRenameTemplate';
import { useExpirationDate } from '@/quote-management/hooks/useExpirationDate';
import { RenameQuoteModal } from '@/quote-management/components/RenameQuoteModal';
import { ExpirationDateModal } from '@/quote-management/components/ExpirationDateModal';
import { formatFileSize, validateFileSize, validateFileType } from '@/quote-management/utils/fileUtils';

export interface ManageNegotiableQuoteTemplateProps
  extends HTMLAttributes<HTMLDivElement> {
  onActionsButtonClick?: (action: string) => void;
  onSendForReview?: (params: {
    templateData?: NegotiableQuoteTemplateModel;
    name?: string;
    comment?: string;
    referenceDocumentLinks?: ReferenceDocumentLinkInput[];
    attachments?: AttachedFile[];
  }) => void;
  maxFiles?: number;
  maxFileSize?: number;
  acceptedFileTypes?: string[];
  slots?: {
    TemplateName?: SlotProps<{
      templateName?: string;
      templateData?: NegotiableQuoteTemplateModel;
      templateDisplayName?: string;
      isRenameDisabled?: boolean;
    }>;
    TemplateStatus?: SlotProps<{
      templateStatus?: string;
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    Banner?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    Details?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    ActionBar?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    ReferenceDocuments?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
      referenceDocuments?: ReferenceDocument[];
      isEditable?: boolean;
      onAddDocument?: () => void;
      onEditDocument?: (document: ReferenceDocument) => void;
      onRemoveDocument?: (document: ReferenceDocument) => void;
      referenceDocumentsTitle?: string;
    }>;
    ItemsTable?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    ItemsQuotedTab?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    CommentsTab?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    HistoryLogTab?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    CommentsTitle?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    Comments?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    AttachFilesField?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
      onFileChange: (files: File[]) => void;
      attachedFiles: AttachedFile[];
      fileUploadError: string | undefined;
      disabled: boolean;
    }>;
    AttachedFilesList?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
      files: AttachedFile[];
      onRemove: (key: string) => void;
      disabled: boolean;
    }>;
    HistoryLogTitle?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    HistoryLog?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    Footer?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
      comment?: string;
      isSubmitting?: boolean;
      attachedFiles?: AttachedFile[];
      referenceDocuments?: ReferenceDocument[];
      hasUnsavedChanges?: boolean;
      handleSendForReview: () => void;
      showAcceptButton?: boolean;
      renameTemplateName?: string;
      renameReason?: string;
    }>;
    ShippingInformationTitle?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
    }>;
    ShippingInformation?: SlotProps<{
      templateData?: NegotiableQuoteTemplateModel;
      loading?: boolean;
      setLoading?: (loading: boolean) => void;
    }>;
  };
}

export const ManageNegotiableQuoteTemplate: Container<ManageNegotiableQuoteTemplateProps> = ({
  slots,
  onActionsButtonClick,
  onSendForReview,
  maxFiles,
  maxFileSize,
  acceptedFileTypes,
  ...props
}) => {
  const [templateData, setTemplateData] = useState<NegotiableQuoteTemplateModel | undefined>(undefined);
  const [loading, setLoading] = useState<boolean>(true);
  const [comment, setComment] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState<boolean>(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState<boolean>(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [cancelSuccess, setCancelSuccess] = useState<boolean>(false);
  const [referenceDocuments, setReferenceDocuments] = useState<ReferenceDocument[]>([]);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState<boolean>(false);
  const [editingDocument, setEditingDocument] = useState<ReferenceDocument | undefined>(undefined);
  const [documentModalErrorMessage, setDocumentModalErrorMessage] = useState<string | null>(null);
  const [showGenerateQuoteModal, setShowGenerateQuoteModal] = useState<boolean>(false);
  const [isGeneratingQuote, setIsGeneratingQuote] = useState<boolean>(false);
  const [generateQuoteSuccessMessage, setGenerateQuoteSuccessMessage] = useState<string>('');
  const [generateQuoteErrorMessage, setGenerateQuoteErrorMessage] = useState<string>('');
  const [showAcceptModal, setShowAcceptModal] = useState<boolean>(false);
  const [isAccepting, setIsAccepting] = useState<boolean>(false);
  const [acceptSuccessMessage, setAcceptSuccessMessage] = useState<string>('');
  const [acceptErrorMessage, setAcceptErrorMessage] = useState<string>('');
  const hasAutoOpened = useRef<boolean>(false);

  // File upload state
  const [attachedFiles, setAttachedFiles] = useState<AttachedFile[]>([]);
  const [fileUploadError, setFileUploadError] = useState<string | undefined>(undefined);

  // Use hook for reference document links logic
  const {
    getReferenceDocumentLinks,
    hasUnsavedChanges: hasUnsavedReferenceDocuments,
  } = useReferenceDocumentLinks({
    templateReferenceDocuments: templateData?.referenceDocuments,
    localReferenceDocuments: referenceDocuments,
  });

  const dictionary = useText({
    createdLabel: 'NegotiableQuoteTemplate.Manage.createdLabel',
    salesRepLabel: 'NegotiableQuoteTemplate.Manage.salesRepLabel',
    expiresLabel: 'NegotiableQuoteTemplate.Manage.expiresLabel',
    templateIdLabel: 'NegotiableQuoteTemplate.Manage.templateIdLabel',
    actionsLabel: 'NegotiableQuoteTemplate.Manage.actionsLabel',
    referenceDocumentsTitle: 'NegotiableQuoteTemplate.Manage.referenceDocuments.title',
    commentsTitle: 'NegotiableQuoteTemplate.Manage.comments.title',
    historyLogTitle: 'NegotiableQuoteTemplate.Manage.historyLog.title',
    itemsQuotedTab: 'NegotiableQuoteTemplate.Manage.tabs.itemsQuoted',
    commentsTab: 'NegotiableQuoteTemplate.Manage.tabs.comments',
    historyLogTab: 'NegotiableQuoteTemplate.Manage.tabs.historyLog',
    templateCommentsPlaceholder: 'NegotiableQuoteTemplate.Manage.templateComments.placeholder',
    sendForReview: 'NegotiableQuoteTemplate.Manage.actionButtons.sendForReview',
    delete: 'NegotiableQuoteTemplate.Manage.actionButtons.delete',
    cancel: 'NegotiableQuoteTemplate.Manage.actionButtons.cancel',
    attachFiles: 'NegotiableQuote.Manage.attachFiles',
    fileUploadError: 'NegotiableQuote.Manage.fileUploadError',
    maxFilesExceeded: 'NegotiableQuote.Manage.maxFilesExceeded',
    maxFileSizeExceeded: 'NegotiableQuote.Manage.maxFileSizeExceeded',
    invalidFileType: 'NegotiableQuote.Manage.invalidFileType',
    confirmationModalDeleteTitle: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.title',
    confirmationModalDeleteMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.message',
    confirmationModalDeleteConfirm: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.confirm',
    confirmationModalDeleteErrorHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.errorHeading',
    confirmationModalDeleteErrorFallback: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.errorFallback',
    confirmationModalDeleteSuccessHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.successHeading',
    confirmationModalDeleteSuccessDescription: 'NegotiableQuoteTemplate.Manage.confirmationModal.delete.successDescription',
    confirmationModalCancelTitle: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.title',
    confirmationModalCancelMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.message',
    confirmationModalCancelConfirm: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.confirm',
    confirmationModalCancelErrorHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.errorHeading',
    confirmationModalCancelErrorFallback: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.errorFallback',
    confirmationModalCancelSuccessHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.successHeading',
    confirmationModalCancelSuccessDescription: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancelTemplate.successDescription',
    confirmationModalCancel: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancel',
    accept: 'NegotiableQuoteTemplate.Manage.actionButtons.accept',
    generateQuote: 'NegotiableQuoteTemplate.Manage.actionButtons.generateQuote',
    generateQuoteModalTitle: 'NegotiableQuoteTemplate.Manage.actionButtons.generateQuote',
    generateQuoteModalMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.message',
    generateQuoteModalCancel: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancel',
    generateQuoteModalConfirm: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.confirm',
    generateQuoteModalConfirmLoading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.confirmLoading',
    generateQuoteModalSuccessHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.successHeading',
    generateQuoteSuccessMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.successDescription',
    generateQuoteModalErrorHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.errorHeading',
    generateQuoteErrorFallback: 'NegotiableQuoteTemplate.Manage.confirmationModal.generateQuote.errorFallback',
    acceptModalTitle: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.title',
    acceptModalMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.message',
    acceptModalCancel: 'NegotiableQuoteTemplate.Manage.confirmationModal.cancel',
    acceptModalConfirm: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.confirm',
    acceptModalConfirmLoading: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.confirmLoading',
    acceptModalSuccessHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.successHeading',
    acceptSuccessMessage: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.successDescription',
    acceptModalErrorHeading: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.errorHeading',
    acceptErrorFallback: 'NegotiableQuoteTemplate.Manage.confirmationModal.accept.errorFallback',
    documentDuplicateUidError: 'NegotiableQuoteTemplate.Manage.referenceDocuments.form.duplicateUidError',
    renameTitle: 'NegotiableQuoteTemplate.Manage.rename.title',
    renameTemplateNameLabel: 'NegotiableQuoteTemplate.Manage.rename.templateNameLabel',
    renameReasonLabel: 'NegotiableQuoteTemplate.Manage.rename.reasonLabel',
    renameButton: 'NegotiableQuoteTemplate.Manage.rename.renameButton',
    renameCancelButton: 'NegotiableQuoteTemplate.Manage.rename.cancelButton',
    renameErrorHeading: 'NegotiableQuoteTemplate.Manage.rename.errorHeading',
    renameTemplateNameRequired: 'NegotiableQuoteTemplate.Manage.rename.templateNameRequired',
    renameErrorDefault: 'NegotiableQuoteTemplate.Manage.rename.errorDefault',
    renameSuccessHeading: 'NegotiableQuoteTemplate.Manage.rename.successHeading',
    renameSuccessMessage: 'NegotiableQuoteTemplate.Manage.rename.successMessage',
    expirationDateRequired: 'NegotiableQuoteTemplate.Manage.expirationDate.expirationDateRequired',
    expirationDateErrorHeading: 'NegotiableQuoteTemplate.Manage.expirationDate.errorHeading',
    expirationDateSuccessHeading: 'NegotiableQuoteTemplate.Manage.expirationDate.successHeading',
    shippingInformationTitle: 'NegotiableQuoteTemplate.Manage.shippingInformation.title',
    unsavedChangesWarningHeading: 'NegotiableQuoteTemplate.Manage.unsavedChangesWarningHeading',
    unsavedChangesWarningMessage: 'NegotiableQuoteTemplate.Manage.unsavedChangesWarningMessage',
    shippingAddressWarningHeading: 'NegotiableQuoteTemplate.Manage.shippingAddressWarningHeading',
    shippingAddressWarningMessage: 'NegotiableQuoteTemplate.Manage.shippingAddressWarningMessage',
  });

  // Use hook for rename template logic (after dictionary is defined)
  const {
    isRenameModalOpen,
    renameTemplateName,
    renameReason,
    renameError,
    renameTemplateNameError,
    renameSuccess,
    handleRenameClick,
    handleRenameClose,
    handleRenameTemplateNameChange,
    handleRenameReasonChange,
    handleRenameSave,
    resetRenameState,
  } = useRenameTemplate({
    initialTemplateName: templateData?.name,
    templateNameRequiredErrorMessage: dictionary.renameTemplateNameRequired,
  });

  // Use hook for expiration date logic
  const {
    isExpirationModalOpen,
    expirationDate,
    expirationError,
    expirationDateError,
    expirationSuccess,
    handleExpirationClick,
    handleExpirationClose,
    handleExpirationDateChange,
    handleExpirationSave,
    resetExpirationState,
  } = useExpirationDate({
    templateId: templateData?.id,
    initialExpirationDate: templateData?.expirationDate,
    expirationDateRequiredErrorMessage: dictionary.expirationDateRequired,
  });

  // Check if there are unsaved rename changes
  // Only check when modal is closed to avoid showing warning while user is still editing
  const hasUnsavedRenameChanges = useMemo(() => {
    // Don't show warning while modal is open
    if (isRenameModalOpen) {
      return false;
    }
    // A rename change exists when renameTemplateName is set and differs from current template name
    return renameTemplateName !== '' && renameTemplateName !== templateData?.name;
  }, [renameTemplateName, templateData?.name, isRenameModalOpen]);

  // Combine unsaved changes from reference documents and rename
  // Note: Expiration date is saved immediately via API, not deferred
  const hasUnsavedChanges = useMemo(() => {
    return hasUnsavedReferenceDocuments || hasUnsavedRenameChanges;
  }, [hasUnsavedReferenceDocuments, hasUnsavedRenameChanges]);

  /**
   * File upload handlers
   */
  const handleAttachFiles = useCallback(async (files: File[]) => {
    // Check maxFiles limit
    if (maxFiles && attachedFiles.length + files.length > maxFiles) {
      setFileUploadError(dictionary.maxFilesExceeded.replace('{maxFiles}', String(maxFiles)));
      return;
    }

    // Validate file types and sizes before uploading
    for (const file of files) {
      if (maxFileSize && !validateFileSize(file.size, maxFileSize)) {
        setFileUploadError(
          dictionary.maxFileSizeExceeded.replace('{maxSize}', formatFileSize(maxFileSize))
        );
        return;
      }

      if (acceptedFileTypes && !validateFileType(file.type, acceptedFileTypes)) {
        setFileUploadError(dictionary.invalidFileType);
        return;
      }
    }

    setFileUploadError(undefined);

    // Add files with 'uploading' status
    const newFiles: AttachedFile[] = files.map(file => ({
      key: `temp-${Date.now()}-${Math.random()}-${file.name}`,
      name: file.name,
      size: file.size,
      status: 'uploading',
    }));

    setAttachedFiles(prev => [...prev, ...newFiles]);

    // Upload each file and update its status
    // NOTE: Backend support not ready yet, so we mock the upload
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const tempKey = newFiles[i].key;

      try {
        const result = await uploadFile(file);
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, key: result.key, status: 'success' }
            : f
        ));
      } catch (error) {
        setAttachedFiles(prev => prev.map(f =>
          f.key === tempKey
            ? { ...f, status: 'error', error: (error as Error).message }
            : f
        ));
      }
    }
  }, [maxFiles, maxFileSize, acceptedFileTypes, attachedFiles, dictionary]);

  const handleRemoveFile = useCallback((key: string) => {
    setAttachedFiles(prev => prev.filter(f => f.key !== key));
  }, []);

  /* Event listeners */
  useEffect(() => {
    const quoteTemplateDataEvent = events.on('quote-management/quote-template-data', (data) => {
      if (data.quoteTemplate) {
        setTemplateData(data.quoteTemplate);
        setLoading(false);
        // Reset comment and submitting state after successful update
        setComment('');
        setIsSubmitting(false);
        // Clear attachments after successful submission
        setAttachedFiles([]);
        // Update reference documents from template data
        setReferenceDocuments(data.quoteTemplate.referenceDocuments || []);
        // Reset rename and expiration state after successful submission
        resetRenameState();
        resetExpirationState();
      }
    }, { eager: true });

    return () => quoteTemplateDataEvent?.off();
  }, [resetRenameState, resetExpirationState]);

  // Initialize reference documents when template data changes
  useEffect(() => {
    if (templateData?.referenceDocuments) {
      setReferenceDocuments(templateData.referenceDocuments);
    }
  }, [templateData?.referenceDocuments]);

  // Auto-open template if status is "Updated"
  useEffect(() => {
    if (templateData && templateData.status === NegotiableQuoteTemplateStatus.UPDATED && !hasAutoOpened.current) {
      hasAutoOpened.current = true;
      const openTemplate = async () => {
        try {
          await openQuoteTemplate({ templateId: templateData.id });
          // The event listener will handle updating state
        } catch (error) {
          console.error('Failed to auto-open quote template:', error);
          // Silently fail - template data is still available for viewing
        }
      };
      openTemplate();
    }
  }, [templateData]);

  /**
   * Handlers
   */
  const handleDeleteCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeleteError(null);
    setDeleteSuccess(false);
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    /* istanbul ignore next */ // If there is no template data, the button should not be enabled or even rendered
    if (!templateData?.id) {
      return;
    }

    setDeleteError(null);
    setDeleteSuccess(false);

    try {
      await deleteQuoteTemplate({
        templateId: templateData.id,
      });
      setDeleteSuccess(true);
      // Close modal after a delay to show success message
      setTimeout(() => {
        setIsDeleteDialogOpen(false);
        setDeleteSuccess(false);
      }, 2000);
    } catch (error) {
      const errorMessage = (error as Error).message || dictionary.confirmationModalDeleteErrorFallback;
      setDeleteError(errorMessage);
    }
  }, [templateData, dictionary.confirmationModalDeleteErrorFallback]);

  const handleDismissError = useCallback(() => {
    setDeleteError(null);
  }, []);

  const handleDismissSuccess = useCallback(() => {
    setDeleteSuccess(false);
  }, []);

  const handleCancelCancel = useCallback(() => {
    setIsCancelDialogOpen(false);
    setCancelError(null);
    setCancelSuccess(false);
  }, []);

  const handleCancelConfirm = useCallback(async () => {
    /* istanbul ignore next */ // If there is no template data, the button should not be enabled or even rendered
    if (!templateData?.id) {
      return;
    }

    setCancelError(null);
    setCancelSuccess(false);

    try {
      await cancelQuoteTemplate({
        templateId: templateData.id,
      });
      setCancelSuccess(true);
      // Close modal after a delay to show success message
      setTimeout(() => {
        setIsCancelDialogOpen(false);
        setCancelSuccess(false);
      }, 2000);
    } catch (error) {
      const errorMessage = (error as Error).message || dictionary.confirmationModalCancelErrorFallback;
      setCancelError(errorMessage);
    }
  }, [templateData, dictionary.confirmationModalCancelErrorFallback]);

  const handleDismissCancelError = useCallback(() => {
    setCancelError(null);
  }, []);

  const handleDismissCancelSuccess = useCallback(() => {
    setCancelSuccess(false);
  }, []);

  const handleSendForReview = useCallback(async () => {
    // Get all reference document links
    const referenceDocumentLinks = getReferenceDocumentLinks();

    // Filter successful attachments
    const successfulAttachments = attachedFiles.filter(f => f.status === 'success');

    // Combine comment with rename reason if rename reason exists
    const combinedComment = [renameReason.trim(), comment.trim()]
      .filter(Boolean)
      .join('\n\n')
      .trim() || undefined;

    // Include renamed name if it differs from current template name
    const templateName = renameTemplateName && renameTemplateName !== templateData?.name
      ? renameTemplateName
      : undefined;

    // If override prop is provided, use it instead of the API
    if (onSendForReview) {
      onSendForReview({
        templateData,
        name: templateName,
        comment: combinedComment,
        referenceDocumentLinks,
        attachments: successfulAttachments.length ? successfulAttachments : undefined,
      });
      return;
    }
    // Otherwise, use the sendQuoteTemplateForReview API
    setIsSubmitting(true);

    try {
      await sendQuoteTemplateForReview({
        templateId: templateData!.id, // the button should be disabled if templateData is null
        name: templateName,
        comment: combinedComment,
        referenceDocumentLinks: referenceDocumentLinks.length > 0 ? referenceDocumentLinks : undefined,
        attachments: successfulAttachments.length ? successfulAttachments.map(f => ({ key: f.key })) : undefined,
      });
      // The quote-template-data event will handle updating state
    } catch (error) {
      console.error('Failed to send quote template for review:', error);
      setIsSubmitting(false);
      // TODO: Show error notification to user
    }
  }, [templateData, onSendForReview, comment, renameReason, renameTemplateName, getReferenceDocumentLinks, attachedFiles]);

  const handleAddDocument = useCallback(() => {
    setEditingDocument(undefined);
    setDocumentModalErrorMessage(null);
    setIsDocumentModalOpen(true);
  }, []);

  const handleEditDocument = useCallback((document: ReferenceDocument) => {
    setEditingDocument(document);
    setDocumentModalErrorMessage(null);
    setIsDocumentModalOpen(true);
  }, []);

  const handleRemoveDocument = useCallback((document: ReferenceDocument) => {
    setReferenceDocuments((prev) => prev.filter((doc) => doc.uid !== document.uid || doc.url !== document.url));
  }, []);

  const handleSaveDocument = useCallback((document: ReferenceDocument) => {
    // Clear any previous error messages
    setDocumentModalErrorMessage(null);

    if (editingDocument) {
      // Update existing document
      setReferenceDocuments((prev) =>
        prev.map((doc) =>
          doc.identifier === editingDocument.identifier
            ? document
            : doc
        )
      );
    } else {
      // Add new document
      setReferenceDocuments((prev) => [...prev, document]);
    }
    setIsDocumentModalOpen(false);
    setEditingDocument(undefined);
  }, [editingDocument]);

  const handleDocumentModalClose = useCallback(() => {
    setIsDocumentModalOpen(false);
    setEditingDocument(undefined);
    setDocumentModalErrorMessage(null);
  }, []);

  // Generate quote handler
  const handleGenerateQuote = useCallback(async () => {
    setIsGeneratingQuote(true);
    setGenerateQuoteErrorMessage('');
    setGenerateQuoteSuccessMessage('');
    try {
      await generateQuoteFromTemplate({ templateId: templateData!.id });
      // Show success message
      setGenerateQuoteSuccessMessage(dictionary.generateQuoteSuccessMessage);
    } catch (error) {
      console.error('Failed to generate quote from template:', error);
      const errorMessage = error instanceof Error ? error.message : dictionary.generateQuoteErrorFallback;
      setGenerateQuoteErrorMessage(errorMessage);
    } finally {
      setIsGeneratingQuote(false);
    }
  }, [templateData, dictionary.generateQuoteSuccessMessage, dictionary.generateQuoteErrorFallback]);

  const handleGenerateQuoteModalCancel = useCallback(() => {
    setShowGenerateQuoteModal(false);
    setGenerateQuoteErrorMessage('');
    setGenerateQuoteSuccessMessage('');
  }, []);

  // Accept quote template handler
  const handleAcceptQuote = useCallback(async () => {
    setIsAccepting(true);
    setAcceptErrorMessage('');
    setAcceptSuccessMessage('');
    try {
      await acceptQuoteTemplate({ templateId: templateData!.id });
      // Show success message
      setAcceptSuccessMessage(dictionary.acceptSuccessMessage);
      // Close modal after a delay to show success message
      setTimeout(() => {
        setShowAcceptModal(false);
        setAcceptSuccessMessage('');
      }, 2000);
    } catch (error) {
      console.error('Failed to accept quote template:', error);
      const errorMessage = error instanceof Error ? error.message : dictionary.acceptErrorFallback;
      setAcceptErrorMessage(errorMessage);
    } finally {
      setIsAccepting(false);
    }
  }, [templateData, dictionary.acceptSuccessMessage, dictionary.acceptErrorFallback]);

  const handleAcceptModalCancel = useCallback(() => {
    setShowAcceptModal(false);
    setAcceptErrorMessage('');
    setAcceptSuccessMessage('');
  }, []);

  /**
   * VNodes for the ManageNegotiableQuoteTemplate component
   */

  // Template name
  const templateName = useMemo(() => {
    // Determine if rename is disabled based on canSendForReview permission
    const isRenameDisabled = !templateData?.canSendForReview;

    // Use the renamed template name if it exists
    const templateDisplayName = renameTemplateName || templateData?.name;

    return (
      <Slot
        name="TemplateName"
        slot={slots?.TemplateName}
        context={{ templateName: templateData?.name, templateData, templateDisplayName, isRenameDisabled }}
      >
        <div className="quote-management-manage-negotiable-quote-template__template-name-wrapper">
          <h1
            data-testid="template-name"
            className="quote-management-manage-negotiable-quote-template__template-name-title"
          >
            {templateDisplayName}
          </h1>
          <button
            onClick={() => handleRenameClick(templateDisplayName)}
            disabled={isRenameDisabled}
            data-testid="rename-template-button"
            className="quote-management-manage-negotiable-quote-template__rename-button"
            aria-label="Rename template"
          >
            <Edit />
          </button>
        </div>
      </Slot>
    );
  }, [slots?.TemplateName, templateData, handleRenameClick, renameTemplateName]);

  // Template Status
  const templateStatus = useMemo(() => {
    return (
      <Slot
        name="TemplateStatus"
        slot={slots?.TemplateStatus}
        context={{ templateStatus: templateData?.status, templateData }}
      >
        <div data-testid="template-status">{templateData?.status}</div>
      </Slot>
    );
  }, [slots?.TemplateStatus, templateData]);

  // Details
  const details = useMemo(() => {
    if (!templateData) {
      return;
    }

    // Determine if expiration date can be edited
    // Match Luma behavior: restrict editing for finalized/terminal statuses
    // Backend may allow these, but we follow Luma's UX for consistency
    const nonEditableStatuses = ['Active', 'Expired', 'Closed', 'Canceled', 'Inactive'];
    const canEditExpirationDate = !nonEditableStatuses.includes(templateData.status);

    // Format expiration date if it exists
    // Expiration date is saved immediately via API, so just use template data
    const formattedExpirationDate = templateData.expirationDate ? formattedDate(templateData.expirationDate, 'short') : undefined;

    return (
      <Slot name="Details" slot={slots?.Details} context={{ templateData }}>
        {/* template id data */}
        <div className="quote-management-manage-negotiable-quote-template__detail">
          <span
            className="quote-management-manage-negotiable-quote-template__detail-title"
            data-testid="template-id-label"
          >
            {dictionary.templateIdLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote-template__detail-content"
            data-testid="template-id-value"
          >
            {templateData.id}
          </span>
        </div>

        <div className="quote-management-manage-negotiable-quote-template__detail">
          <span
            className="quote-management-manage-negotiable-quote-template__detail-title"
            data-testid="created-label"
          >
            {dictionary.createdLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote-template__detail-content"
            data-testid="created-value"
          >
            {formattedDate(templateData.createdAt, 'short')} (
            {templateData.buyer.firstname} {templateData.buyer.lastname})
          </span>
        </div>

        <div className="quote-management-manage-negotiable-quote-template__detail">
          <span
            className="quote-management-manage-negotiable-quote-template__detail-title"
            data-testid="sales-rep-label"
          >
            {dictionary.salesRepLabel}
          </span>
          <span
            className="quote-management-manage-negotiable-quote-template__detail-content"
            data-testid="sales-rep-value"
          >
            {templateData.salesRepName}
          </span>
        </div>

        <div className="quote-management-manage-negotiable-quote-template__detail">
          <span
            className="quote-management-manage-negotiable-quote-template__detail-title"
            data-testid="expires-label"
          >
            {dictionary.expiresLabel}
          </span>
          <div className="quote-management-manage-negotiable-quote-template__expiration-wrapper">
            <span
              className="quote-management-manage-negotiable-quote-template__detail-content"
              data-testid="expires-value"
            >
              {formattedExpirationDate}
            </span>
            {canEditExpirationDate && (
              <button
                onClick={() => handleExpirationClick(templateData.expirationDate)}
                className="quote-management-manage-negotiable-quote-template__edit-expiration-button"
                data-testid="edit-expiration-button"
                aria-label="Edit expiration date"
              >
                <Edit />
              </button>
            )}
          </div>
        </div>
      </Slot>
    );
  }, [slots?.Details, dictionary, templateData, handleExpirationClick]);


  // Action bar
  const buttons: { label: string; value: string; disabled?: boolean }[] =
    useMemo(() => {
      return [
        {
          label: dictionary.generateQuote,
          value: 'generateQuote',
          disabled: !templateData?.canGenerateQuoteFromTemplate,
        },
      ];
    }, [dictionary, templateData?.canGenerateQuoteFromTemplate]);

  const actionBar = useMemo(() => {
    const handleActionsButtonClick = (action: string) => {
      switch (action) {
        case 'generateQuote':
          setShowGenerateQuoteModal(true);
          setGenerateQuoteErrorMessage(''); // Clear modal errors
          setGenerateQuoteSuccessMessage(''); // Clear modal success messages
          onActionsButtonClick?.(action);
          break;
      }
    };

    return (
      <Slot
        name="ActionBar"
        slot={slots?.ActionBar}
        context={{ templateData }}
      >
        <ActionsBar
          dropdownPlaceholder={dictionary.actionsLabel}
          dropdownOptions={[]}
          buttons={buttons.map((button) => (
            <Button
              key={button.value}
              variant="tertiary"
              onClick={() => handleActionsButtonClick(button.value)}
              data-testid={`actions-bar-${button.value}-button`}
              data-action={button.value}
              disabled={button.disabled}
            >
              {button.label}
            </Button>
          ))}
        />
      </Slot>
    );
  }, [
    buttons,
    dictionary,
    slots?.ActionBar,
    templateData,
    onActionsButtonClick,
  ]);

  // Reference documents
  const isEditable = useMemo(() => {
    const status = templateData?.status as string;
    return status === 'Draft' || status === 'Open';
  }, [templateData?.status]);


  const referenceDocumentsNode = useMemo(() => {
    const shouldShowTitle = referenceDocuments.length > 0 || isEditable;
    const title = shouldShowTitle ? dictionary.referenceDocumentsTitle : undefined;

    return (
      <Slot
        name="ReferenceDocuments"
        slot={slots?.ReferenceDocuments}
        context={{ templateData, referenceDocuments, isEditable, onAddDocument: handleAddDocument, onEditDocument: handleEditDocument, onRemoveDocument: handleRemoveDocument, referenceDocumentsTitle: dictionary.referenceDocumentsTitle }}
      >
        <div data-testid="reference-documents">
          {shouldShowTitle && <h2>{title}</h2>}
          <ReferenceDocumentsList
            documents={referenceDocuments}
            isEditable={isEditable}
            onAdd={handleAddDocument}
            onEdit={handleEditDocument}
            onRemove={handleRemoveDocument}
          />
        </div>
      </Slot>
    );
  }, [slots?.ReferenceDocuments, dictionary, templateData, referenceDocuments, isEditable, handleAddDocument, handleEditDocument, handleRemoveDocument]);

  // Shipping information title
  const shippingInformationTitle = useMemo(() => {
    return (
      <Slot
        name="ShippingInformationTitle"
        slot={slots?.ShippingInformationTitle}
        context={{ templateData }}
      >
        <h2>{dictionary.shippingInformationTitle}</h2>
      </Slot>
    );
  }, [slots?.ShippingInformationTitle, templateData, dictionary]);

  // Shipping information
  const shippingInformation = useMemo(() => {
    return (
      <Slot
        name="ShippingInformation"
        slot={slots?.ShippingInformation}
        context={{ templateData, loading, setLoading }}
      >
        <ShippingAddressDisplay
          shippingAddress={templateData?.shippingAddresses?.[0]}
          loading={loading}
          data-testid="manage-template-shipping-address"
        />
      </Slot>
    );
  }, [slots?.ShippingInformation, templateData, loading, setLoading]);

  // Items table (using TabbedContent with ItemsQuoted)
  const itemsTable = useMemo(() => {
    return (
      <Slot
        name="ItemsTable"
        slot={slots?.ItemsTable}
        context={{ templateData }}
      >
        <TabbedContent
          tabs={
            new Map([
              ['items-quoted', dictionary.itemsQuotedTab],
              ['comments', dictionary.commentsTab],
              ['history-log', dictionary.historyLogTab],
            ])
          }
          tabsContent={
            new Map([
              [
                'items-quoted',
                <Slot
                  key="items-quoted"
                  name="ItemsQuotedTab"
                  slot={slots?.ItemsQuotedTab}
                  context={{ templateData }}
                >
                  <ItemsQuotedTemplate templateData={templateData} />
                </Slot>,
              ],
              [
                'comments',
                <Slot
                  key="comments"
                  name="CommentsTab"
                  slot={slots?.CommentsTab}
                  context={{ templateData }}
                >
                  <QuoteTemplateCommentsList templateData={templateData} />
                </Slot>,
              ],
              [
                'history-log',
                <Slot
                  key="history-log"
                  name="HistoryLogTab"
                  slot={slots?.HistoryLogTab}
                  context={{ templateData }}
                >
                  <QuoteTemplateHistoryLog templateData={templateData} />
                </Slot>,
              ],
            ])
          }
        />
      </Slot>
    );
  }, [
    slots?.ItemsTable,
    slots?.ItemsQuotedTab,
    slots?.CommentsTab,
    slots?.HistoryLogTab,
    dictionary,
    templateData,
  ]);

  // Comments title
  const commentsTitle = useMemo(() => {
    return (
      <Slot
        name="CommentsTitle"
        slot={slots?.CommentsTitle}
        context={{ templateData }}
      >
        <h2>{dictionary.commentsTitle}</h2>
      </Slot>
    );
  }, [slots?.CommentsTitle, dictionary, templateData]);

  // Comments
  const comments = useMemo(() => {
    return (
      <Slot
        name="Comments"
        slot={slots?.Comments}
        context={{ templateData }}
      >
        <TextArea
          name="templateComments"
          placeholder={dictionary.templateCommentsPlaceholder}
          rows={3}
          value={comment}
          onInput={(e) => {
            setComment((e.target as HTMLTextAreaElement).value);
          }}
          disabled={!templateData?.canSendForReview || isSubmitting}
        />
      </Slot>
    );
  }, [slots?.Comments, dictionary, templateData, comment, isSubmitting]);

  // File upload should be disabled when template cannot be sent for review
  const isFileUploadDisabled = useMemo(() => {
    return !templateData?.canSendForReview;
  }, [templateData]);

  // File upload input
  const attachFilesField = useMemo(() => {
    // Check if button should be visually disabled
    const isDisabled = isSubmitting || isFileUploadDisabled;

    return (
      <Slot
        name="AttachFilesField"
        slot={slots?.AttachFilesField}
        context={{ templateData, onFileChange: handleAttachFiles, attachedFiles, fileUploadError, disabled: isDisabled }}
      >
        <div 
          className="quote-management-manage-negotiable-quote-template__attach-files"
          {...(isDisabled ? { 'data-disabled': true } : {})}
          aria-disabled={isDisabled}
        >
          <InputFile
            id="manage-template-attach-files"
            multiple
            onChange={(e: Event) => {
              const target = e.target as HTMLInputElement;
              const fileList = target?.files;
              const files = fileList ? Array.from(fileList) : [];
              if (files.length > 0) {
                void handleAttachFiles(files);
              }
            }}
            icon={<Add />}
            disabled={isDisabled}
            data-testid="manage-template-attach-files"
            accept={acceptedFileTypes?.join(',')}
          >
            {dictionary.attachFiles}
          </InputFile>
          {fileUploadError && (
            <div
              className="quote-management-manage-negotiable-quote-template__file-error"
              data-testid="file-upload-error"
            >
              {fileUploadError}
            </div>
          )}
        </div>
      </Slot>
    );
  }, [
    slots?.AttachFilesField,
    templateData,
    handleAttachFiles,
    isSubmitting,
    isFileUploadDisabled,
    acceptedFileTypes,
    fileUploadError,
    dictionary,
    attachedFiles,
  ]);

  // Attached files list
  const attachedFilesList = useMemo(() => {
    if (!attachedFiles.length) return undefined;

    return (
      <Slot
        name="AttachedFilesList"
        slot={slots?.AttachedFilesList}
        context={{ templateData, files: attachedFiles, onRemove: handleRemoveFile, disabled: isSubmitting }}
      >
        <AttachedFilesList
          files={attachedFiles}
          onRemove={handleRemoveFile}
          disabled={isSubmitting}
        />
      </Slot>
    );
  }, [attachedFiles, handleRemoveFile, isSubmitting, slots?.AttachedFilesList, templateData]);

  // Unsaved changes warning banner
  const unsavedChangesBanner = useMemo(
    () =>
      hasUnsavedChanges ? (
        <InLineAlert
          type="warning"
          variant="primary"
          icon={<WarningFilled />}
          heading={dictionary.unsavedChangesWarningHeading}
          description={dictionary.unsavedChangesWarningMessage}
          data-testid="unsaved-changes-warning-alert"
        />
      ) : undefined,
    [hasUnsavedChanges, dictionary.unsavedChangesWarningHeading, dictionary.unsavedChangesWarningMessage]
  );

  // Shipping address warning banner
  const shippingAddressWarningBanner = useMemo(() => {
    return templateData?.shippingAddresses?.length ? undefined : (
      <InLineAlert
        type="warning"
        variant="primary"
        icon={<WarningFilled />}
        heading={dictionary.shippingAddressWarningHeading}
        description={dictionary.shippingAddressWarningMessage}
        data-testid="shipping-address-warning-alert"
      />
    );
  }, [templateData?.shippingAddresses?.length, dictionary.shippingAddressWarningHeading, dictionary.shippingAddressWarningMessage]);

  const banners = useMemo(() => {
    return <>
      {unsavedChangesBanner}
      {shippingAddressWarningBanner}
    </>
  }, [unsavedChangesBanner, shippingAddressWarningBanner]);

  // Footer
  const footer = useMemo(() => {
    const showAcceptButton = templateData?.status === NegotiableQuoteTemplateStatus.OPEN && templateData?.canAccept;

    return (
      <Slot
        name="Footer"
        slot={slots?.Footer}
        context={{ templateData, comment, isSubmitting, attachedFiles, referenceDocuments, hasUnsavedChanges, handleSendForReview, showAcceptButton, renameTemplateName, renameReason }}
      >
        {showAcceptButton && (
          <Button
            variant="primary"
            onClick={() => {
              setShowAcceptModal(true);
              setAcceptErrorMessage(''); // Clear modal errors
              setAcceptSuccessMessage(''); // Clear modal success messages
            }}
            data-testid="accept-button"
            disabled={isSubmitting || hasUnsavedChanges}
          >
            {dictionary.accept}
          </Button>
        )}

        {templateData?.canSendForReview && (
          <Button
            variant="primary"
            onClick={handleSendForReview}
            data-testid="send-for-review-button"
            disabled={!templateData?.canSendForReview || isSubmitting}
          >
            {dictionary.sendForReview}
          </Button>
        )}
        {templateData?.canCancel && (
          <Button
            variant="tertiary"
            onClick={() => setIsCancelDialogOpen(true)}
            data-testid="cancel-template-button"
            disabled={isSubmitting}
          >
            {dictionary.cancel}
          </Button>
        )}
        {templateData?.canDelete && (
          <Button
            variant="tertiary"
            onClick={() => setIsDeleteDialogOpen(true)}
            data-testid="delete-template-button"
            disabled={isSubmitting}
          >
            {dictionary.delete}
          </Button>
        )}
      </Slot>
    );
  }, [
    slots?.Footer,
    dictionary,
    templateData,
    comment,
    isSubmitting,
    attachedFiles,
    handleSendForReview,
    referenceDocuments,
    hasUnsavedChanges,
    renameTemplateName,
    renameReason,
  ]);

  // Delete error banner
  const deleteErrorBanner = deleteError ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.confirmationModalDeleteErrorHeading}
      description={deleteError}
      onDismiss={handleDismissError}
      data-testid="confirmation-modal-error-banner"
    />
  ) : null;

  // Delete success banner
  const deleteSuccessBanner = deleteSuccess ? (
    <InLineAlert
      type="success"
      variant="primary"
      icon={<CheckWithCircle />}
      heading={dictionary.confirmationModalDeleteSuccessHeading}
      description={dictionary.confirmationModalDeleteSuccessDescription}
      onDismiss={handleDismissSuccess}
      data-testid="confirmation-modal-success-banner"
    />
  ) : null;

  const deleteBanner = deleteSuccessBanner || deleteErrorBanner;

  // Cancel error banner
  const cancelErrorBanner = cancelError ? (
    <InLineAlert
      type="error"
      variant="primary"
      icon={<WarningFilled />}
      heading={dictionary.confirmationModalCancelErrorHeading}
      description={cancelError}
      onDismiss={handleDismissCancelError}
      data-testid="confirmation-modal-cancel-error-banner"
    />
  ) : null;

  // Cancel success banner
  const cancelSuccessBanner = cancelSuccess ? (
    <InLineAlert
      type="success"
      variant="primary"
      icon={<CheckWithCircle />}
      heading={dictionary.confirmationModalCancelSuccessHeading}
      description={dictionary.confirmationModalCancelSuccessDescription}
      onDismiss={handleDismissCancelSuccess}
      data-testid="confirmation-modal-cancel-success-banner"
    />
  ) : null;

  const cancelBanner = cancelSuccessBanner || cancelErrorBanner;

  // Rename modal error banner
  const renameErrorBanner = useMemo(
    () =>
      renameError ? (
        <InLineAlert
          type="error"
          variant="primary"
          icon={<WarningFilled color="var(--color-error)" />}
          heading={dictionary.renameErrorHeading}
          description={renameError}
        />
      ) : undefined,
    [renameError, dictionary.renameErrorHeading]
  );

  // Rename modal success banner
  const renameSuccessBanner = useMemo(
    () =>
      renameSuccess ? (
        <InLineAlert
          type="success"
          variant="primary"
          icon={<CheckWithCircle />}
          heading={dictionary.renameSuccessHeading}
          description={renameSuccess}
        />
      ) : undefined,
    [renameSuccess, dictionary.renameSuccessHeading]
  );

  // Expiration date modal error banner
  const expirationErrorBanner = useMemo(
    () =>
      expirationError ? (
        <InLineAlert
          type="error"
          variant="primary"
          icon={<WarningFilled color="var(--color-error)" />}
          heading={dictionary.expirationDateErrorHeading}
          description={expirationError}
        />
      ) : undefined,
    [expirationError, dictionary.expirationDateErrorHeading]
  );

  // Expiration date modal success banner
  const expirationSuccessBanner = useMemo(
    () =>
      expirationSuccess ? (
        <InLineAlert
          type="success"
          variant="primary"
          icon={<CheckWithCircle />}
          heading={dictionary.expirationDateSuccessHeading}
          description={expirationSuccess}
        />
      ) : undefined,
    [expirationSuccess, dictionary.expirationDateSuccessHeading]
  );

  const manageNegotiableQuoteTemplateProps: ManageNegotiableQuoteTemplateComponentProps = {
    ...props,
    loading,
    templateName,
    templateStatus,
    banner: banners,
    details,
    actionBar,
    referenceDocuments: referenceDocumentsNode,
    itemsTable,
    shippingInformationTitle,
    shippingInformation,
    commentsTitle,
    comments,
    attachFilesField,
    attachedFilesList,
    footer,
  };

  // Create banner for generate quote modal based on success/error state
  let generateQuoteModalBanner = null;
  if (generateQuoteSuccessMessage) {
    generateQuoteModalBanner = (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<Icon source="CheckWithCircle" />}
        heading={dictionary.generateQuoteModalSuccessHeading}
        description={generateQuoteSuccessMessage}
        data-testid="generate-quote-success-banner"
      />
    );
  } else if (generateQuoteErrorMessage) {
    generateQuoteModalBanner = (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<Icon source="WarningFilled" />}
        heading={dictionary.generateQuoteModalErrorHeading}
        description={generateQuoteErrorMessage}
        data-testid="generate-quote-error-banner"
      />
    );
  }

  // Create banner for accept modal based on success/error state
  let acceptModalBanner = null;
  if (acceptSuccessMessage) {
    acceptModalBanner = (
      <InLineAlert
        type="success"
        variant="primary"
        icon={<Icon source="CheckWithCircle" />}
        heading={dictionary.acceptModalSuccessHeading}
        description={acceptSuccessMessage}
        data-testid="accept-success-banner"
      />
    );
  } else if (acceptErrorMessage) {
    acceptModalBanner = (
      <InLineAlert
        type="error"
        variant="primary"
        icon={<Icon source="WarningFilled" />}
        heading={dictionary.acceptModalErrorHeading}
        description={acceptErrorMessage}
        data-testid="accept-error-banner"
      />
    );
  }

  return (
    <>
      <ManageNegotiableQuoteTemplateComponent {...manageNegotiableQuoteTemplateProps} />
      {isDeleteDialogOpen &&
        createPortal(
          <ConfirmationModal
            open={isDeleteDialogOpen}
            title={dictionary.confirmationModalDeleteTitle}
            message={dictionary.confirmationModalDeleteMessage}
            cancelLabel={dictionary.confirmationModalCancel}
            confirmLabel={dictionary.confirmationModalDeleteConfirm}
            onCancel={handleDeleteCancel}
            onConfirm={handleDeleteConfirm}
            showCloseButton={!deleteBanner}
            confirmationBanner={deleteBanner}
          />,
          document.body
        )}
      {isCancelDialogOpen &&
        createPortal(
          <ConfirmationModal
            open={isCancelDialogOpen}
            title={dictionary.confirmationModalCancelTitle}
            message={dictionary.confirmationModalCancelMessage}
            cancelLabel={dictionary.confirmationModalCancel}
            confirmLabel={dictionary.confirmationModalCancelConfirm}
            onCancel={handleCancelCancel}
            onConfirm={handleCancelConfirm}
            showCloseButton={!cancelBanner}
            confirmationBanner={cancelBanner}
          />,
          document.body
        )}
      {isDocumentModalOpen &&
        createPortal(
          <ReferenceDocumentFormModal
            open={isDocumentModalOpen}
            document={editingDocument}
            errorMessage={documentModalErrorMessage || undefined}
            onSave={handleSaveDocument}
            onClose={handleDocumentModalClose}
            isSubmitting={isSubmitting}
          />,
          document.body
        )}
      {showGenerateQuoteModal &&
        createPortal(
          <ConfirmationModal
            open={showGenerateQuoteModal}
            title={dictionary.generateQuoteModalTitle}
            message={dictionary.generateQuoteModalMessage}
            cancelLabel={dictionary.generateQuoteModalCancel}
            confirmLabel={
              isGeneratingQuote
                ? dictionary.generateQuoteModalConfirmLoading
                : dictionary.generateQuoteModalConfirm
            }
            onCancel={handleGenerateQuoteModalCancel}
            onConfirm={isGeneratingQuote ? undefined : handleGenerateQuote}
            showCloseButton={true}
            confirmationBanner={generateQuoteModalBanner}
            data-testid="generate-quote-confirmation-modal"
          />,
          document.body
        )}
      {showAcceptModal &&
        createPortal(
          <ConfirmationModal
            open={showAcceptModal}
            title={dictionary.acceptModalTitle}
            message={dictionary.acceptModalMessage}
            cancelLabel={dictionary.acceptModalCancel}
            confirmLabel={
              isAccepting
                ? dictionary.acceptModalConfirmLoading
                : dictionary.acceptModalConfirm
            }
            onCancel={handleAcceptModalCancel}
            onConfirm={isAccepting ? undefined : handleAcceptQuote}
            showCloseButton={true}
            confirmationBanner={acceptModalBanner}
            data-testid="accept-confirmation-modal"
          />,
          document.body
        )}
      {isRenameModalOpen &&
        createPortal(
          <RenameQuoteModal
            open={isRenameModalOpen}
            quoteName={renameTemplateName}
            renameReason={renameReason}
            quoteNameError={renameTemplateNameError}
            errorBanner={renameErrorBanner}
            successBanner={renameSuccessBanner}
            onQuoteNameChange={handleRenameTemplateNameChange}
            onRenameReasonChange={handleRenameReasonChange}
            onSave={handleRenameSave}
            onClose={handleRenameClose}
          />,
          document.body
        )}
      {isExpirationModalOpen &&
        createPortal(
          <ExpirationDateModal
            open={isExpirationModalOpen}
            expirationDate={expirationDate}
            expirationDateError={expirationDateError}
            errorBanner={expirationErrorBanner}
            successBanner={expirationSuccessBanner}
            onExpirationDateChange={handleExpirationDateChange}
            onSave={handleExpirationSave}
            onClose={handleExpirationClose}
          />,
          document.body
        )}
    </>
  );
};
